import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const related = getRelatedArticles(article.id, article.categorySlug);
  const category = categories.find((c) => c.slug === article.categorySlug);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `http://lalaccu.cn/article/${article.id}`,
        headline: article.title,
        description: article.summary,
        image: article.image,
        datePublished: article.date,
        dateModified: article.date,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: '51app',
          logo: {
            '@type': 'ImageObject',
            url: 'http://lalaccu.cn/logo.png',
          },
        },
        isPartOf: {
          '@type': 'WebSite',
          name: '51app',
          url: 'http://lalaccu.cn/',
        },
        articleSection: article.category,
        keywords: article.tags.join(', '),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'http://lalaccu.cn/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: article.category,
            item: `http://lalaccu.cn/category/${article.categorySlug}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
            item: `http://lalaccu.cn/article/${article.id}`,
          },
        ],
      },
    ],
  };

  const contentParagraphs = article.content.split('\n\n');

  return (
    <>
      <SEOHead
        title={`${article.title} - 51app`}
        description={`${article.summary} 51app 全网吃瓜爆料平台，免费浏览更多${article.category}内容。`}
        keywords={`${article.tags.join(',')},51app,吃瓜爆料,免费app,${article.category},51app下载`}
        canonical={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schema={schema}
      />
      <Navbar />
      <main className="pt-24 md:pt-28">
        <div className="px-4 md:px-8 max-w-4xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                {
                  label: article.category,
                  href: `/category/${article.categorySlug}`,
                },
                { label: article.title },
              ]}
            />
          </div>

          <article itemScope itemType="https://schema.org/Article">
            {/* Header */}
            <header className="mb-6 md:mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors"
                >
                  {article.category}
                </Link>
                {article.isHot && (
                  <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    热门
                  </span>
                )}
              </div>

              <h1
                className="font-heading text-2xl md:text-3xl lg:text-4xl text-primary-500 leading-snug"
                itemProp="headline"
              >
                {article.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                <span itemProp="author" itemScope itemType="https://schema.org/Person">
                  <span itemProp="name">{article.author}</span>
                </span>
                <span>·</span>
                <time itemProp="datePublished" dateTime={article.date}>
                  {article.date}
                </time>
                <span>·</span>
                <span>{article.views.toLocaleString()} 阅读</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </header>

            {/* Featured Image */}
            <figure className="mb-6 md:mb-8 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover object-center"
                itemProp="image"
              />
              <figcaption className="sr-only">{article.title} 配图</figcaption>
            </figure>

            {/* Content */}
            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              itemProp="articleBody"
            >
              {contentParagraphs.map((paragraph, index) => (
                <p key={index} className="mb-4 text-base md:text-lg leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Share */}
            <div className="mt-8 pt-6 border-t border-background-200/70">
              <p className="text-sm text-foreground-500 mb-3">分享这篇爆料：</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 text-foreground-600 hover:text-accent-600 transition-colors"
                  aria-label="分享到微信"
                >
                  <i className="ri-wechat-fill w-4 h-4 flex items-center justify-center" />
                </button>
                <button
                  type="button"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 text-foreground-600 hover:text-accent-600 transition-colors"
                  aria-label="保存到站内动态"
                >
                  <i className="ri-chat-1-fill w-4 h-4 flex items-center justify-center" />
                </button>
                <button
                  type="button"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 text-foreground-600 hover:text-accent-600 transition-colors"
                  aria-label="复制链接"
                >
                  <i className="ri-link w-4 h-4 flex items-center justify-center" />
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {related.length > 0 && (
            <aside className="mt-12 md:mt-16 pt-8 border-t border-background-200/70" aria-label="相关推荐">
              <h2 className="font-heading text-xl md:text-2xl text-primary-500 mb-6">
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group flex gap-3 rounded-lg bg-background-100 p-3 hover:bg-background-200/70 transition-colors"
                  >
                    <div className="shrink-0 w-20 h-16 rounded-md overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400">
                        {ra.views.toLocaleString()} 阅读
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </aside>
          )}

          {/* Category Link */}
          {category && (
            <div className="mt-8 text-center">
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-primary-500 text-primary-500 text-sm font-medium hover:bg-primary-50 transition-colors"
              >
                查看更多{category.name}
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
